/**
 * Allocation Skeleton
 * Loading skeleton for allocation views
 */
import Card from '@/components/ui/Card';

interface AllocationSkeletonProps {
  tab: 'portfolio' | 'buckets' | 'targets' | 'holdings' | 'warnings';
}

export default function AllocationSkeleton({ tab }: AllocationSkeletonProps) {
  switch (tab) {
    case 'portfolio':
      return <PortfolioSkeleton />;
    case 'buckets':
      return <BucketsSkeleton />;
    case 'targets':
      return <TargetsSkeleton />;
    case 'holdings':
      return <HoldingsSkeleton />;
    case 'warnings':
      return <WarningsSkeleton />;
    default:
      return <GenericSkeleton />;
  }
}

function PortfolioSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-32" />
              </div>
            ))}
          </div>
        </div>
      </Card>
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-3 h-3 bg-gray-200 rounded-full" />
                <div className="flex-1 h-10 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function BucketsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-32" />
            </div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-12" />
            </div>
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i}>
            <div className="p-4 animate-pulse">
              <div className="flex items-center justify-between mb-3">
                <div className="w-3 h-3 bg-gray-200 rounded-full" />
                <div className="h-4 bg-gray-200 rounded w-12" />
              </div>
              <div className="h-6 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-3" />
              <div className="h-2 bg-gray-200 rounded-full" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function TargetsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-12" />
              </div>
            ))}
          </div>
        </div>
      </Card>
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function HoldingsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
        </div>
      </Card>
      <div className="h-10 bg-gray-100 rounded w-full" />
      <Card>
        <div className="p-6 animate-pulse">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function WarningsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded w-16 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-10" />
              </div>
            ))}
          </div>
        </div>
      </Card>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <div className="p-4 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-5 h-5 bg-gray-200 rounded" />
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function GenericSkeleton() {
  return (
    <Card>
      <div className="p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    </Card>
  );
}
